"use client";
import React, { useState, useEffect, FC } from "react";

import dynamic from "next/dynamic";

import { PopperProps } from "@mui/base/Popper";

const Popper = dynamic(() => import("@mui/base").then((mod) => mod.Popper));

interface TooltipProps {
  /**
   * The anchor element for the tooltip.
   */
  anchorEl: PopperProps["anchorEl"];
  /**
   * The title of the tooltip.
   */
  title: string;
}
interface TooltipProps {
  anchorEl: PopperProps["anchorEl"];
  title: string;
}
export const Tooltip: FC<TooltipProps> = ({ anchorEl, title }) => {
  const [isOpened, setIsOpened] = useState(false);
  const [isTouchscreen, setIsTouchscreen] = useState(false);

  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpened(false);
    }
  };

  useEffect(() => {
    setIsTouchscreen(
      window.matchMedia("(hover: none)").matches ||
        window.matchMedia("(pointer: coarse)").matches,
    );
  }, []);

  useEffect(() => {
    if (isOpened) {
      // close the tooltip on ESC
      document.addEventListener("keydown", handleEsc);
      return () => {
        document.removeEventListener("keydown", handleEsc);
      };
    }
  }, [isOpened]);

  useEffect(() => {
    if (anchorEl && anchorEl instanceof HTMLElement) {
      const handleMouseEnter = () => setIsOpened(true);
      const handleMouseLeave = () => setIsOpened(false);
      const handleFocus = () => setIsOpened(true);
      const handleBlur = () => setIsOpened(false);

      anchorEl.addEventListener("mouseenter", handleMouseEnter);
      anchorEl.addEventListener("mouseleave", handleMouseLeave);
      anchorEl.addEventListener("focus", handleFocus);
      anchorEl.addEventListener("blur", handleBlur);

      return () => {
        anchorEl.removeEventListener("mouseenter", handleMouseEnter);
        anchorEl.removeEventListener("mouseleave", handleMouseLeave);
        anchorEl.removeEventListener("focus", handleFocus);
        anchorEl.removeEventListener("blur", handleBlur);
      };
    }
  }, [anchorEl]);

  return isTouchscreen || !isOpened ? null : (
    <Popper anchorEl={anchorEl} className="z-[1300] font-sans" open={isOpened}>
      <div
        className={`relative mt-[14px] max-w-[300px] rounded-[4px] bg-[#515557] px-2 py-1 text-sm font-normal text-white transition-opacity delay-200 ${
          isOpened ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="absolute left-[50%] top-0 z-[-1] size-[1em] translate-x-[-50%] translate-y-[50%] rotate-45 bg-[#515557]" />
        {title}
      </div>
    </Popper>
  );
};

export default Tooltip;
